(function () {
  "use strict";

  const CONFIG_SCRIPT_ID = "qk-lubrication-config";
  const PDP_SELECTOR = "[data-qk-lube-pdp]";

  const PRODUCT_TYPE_KEYBOARD = "Keyboards";
  const SWITCH_TAG_ALIAS = "switchuri";
  const LUBE_SCOPE_KEYBOARD = "keyboard";
  const LUBE_SCOPE_SWITCH = "switch";
  const PRODUCT_LOOKUP_PAGE_LIMIT = 250;
  const PRODUCT_LOOKUP_MAX_PAGES = 80;
  const LUBE_INTENT_STORAGE_KEY = "qk:lube-intent:v1";

  const PROP_SELECTED = "selected";
  const PROP_SCOPE = "scope";
  const PROP_RULE = "rule";
  const PROP_LAYOUT = "layout";
  const PROP_KEYS = [PROP_SELECTED, PROP_SCOPE, PROP_RULE, PROP_LAYOUT];

  const variantCacheByProductId = {};
  const productLookupCacheById = {};

  function init() {
    const nodes = Array.from(document.querySelectorAll(PDP_SELECTOR));
    if (!nodes.length) {
      return;
    }

    const config = normalizeConfig(parseJsonScript(CONFIG_SCRIPT_ID));
    nodes.forEach((node) => {
      try {
        initNode(node, config);
      } catch (error) {
        console.error("[qk-lubrication-pdp] Init failed", error);
        node.hidden = true;
      }
    });
  }

  function initNode(node, config) {
    if (!(node instanceof HTMLElement)) {
      return;
    }

    const checkbox = node.querySelector("[data-qk-lube-checkbox]");
    const label = node.querySelector("[data-qk-lube-label]");
    const priceLabel = node.querySelector("[data-qk-lube-price]");
    const unavailable = node.querySelector("[data-qk-lube-unavailable]");

    if (!(checkbox instanceof HTMLInputElement)) {
      node.hidden = true;
      return;
    }

    const context = getNodeContext(node);
    const form = context.formId ? document.getElementById(context.formId) : null;
    if (!(form instanceof HTMLFormElement)) {
      node.hidden = true;
      return;
    }
    clearLegacyPublicFormPropertyInputs(form);
    const formPropertyInputs = ensureFormPropertyInputs(form);
    const keyboardMapping = resolveKeyboardMapping(context.layoutRaw, config.keyboard.rules);
    const switchRule = resolveSwitchRule(config.switch.rules);
    const isKeyboardFlowActive =
      config.enabled &&
      config.keyboard.enabled &&
      context.productType === PRODUCT_TYPE_KEYBOARD &&
      !context.noLubrication &&
      isTruthyHotswap(context.hotswapRaw) &&
      Boolean(keyboardMapping);

    const isSwitchFlowActive =
      config.enabled &&
      config.switch.enabled &&
      context.switchTagEligible &&
      !context.noLubrication &&
      Boolean(switchRule);

    if (!isKeyboardFlowActive && !isSwitchFlowActive) {
      clearFormPropertyInputs(formPropertyInputs);
      node.hidden = true;
      setDynamicCheckoutDisabled(context.formId, false);
      return;
    }

    node.hidden = false;
    if (label) {
      label.textContent = String(config.ui.label_ro || "Lubrifiere premium pentru switch-uri");
    }

    if (unavailable instanceof HTMLElement) {
      unavailable.hidden = true;
      unavailable.textContent = "";
    }

    const flow = isKeyboardFlowActive
      ? {
          scope: LUBE_SCOPE_KEYBOARD,
          rule: keyboardMapping.rule,
          layoutKey: keyboardMapping.layoutKey,
        }
      : {
          scope: LUBE_SCOPE_SWITCH,
          rule: switchRule,
          layoutKey: "",
        };

    setFormLubricationMetadata(form, {
      scope: flow.scope,
      ruleKey: flow.rule ? flow.rule.key : "",
      variantLegacyId: "",
    });

    const syncAndPersistState = () => {
      syncPropertyState(formPropertyInputs, {
        selected: checkbox.checked && !checkbox.disabled,
        scope: flow.scope,
        ruleKey: flow.rule ? flow.rule.key : "",
        layoutKey: flow.layoutKey,
      });

      persistPendingLubricationIntent({
        selected: checkbox.checked && !checkbox.disabled,
        form,
        context,
        flow,
      });

      setDynamicCheckoutDisabled(context.formId, checkbox.checked && !checkbox.disabled);
    };

    bindSubmissionGuards({
      form,
      checkbox,
      flow,
      context,
      getRuleKey: () => (flow.rule ? flow.rule.key : ""),
      getLayoutKey: () => flow.layoutKey,
      primeState: syncAndPersistState,
    });

    checkbox.checked = false;
    checkbox.disabled = true;
    if (priceLabel) {
      priceLabel.textContent = "(indisponibil)";
    }

    syncAndPersistState();

    if (!flow.rule || !flow.rule.productLegacyId) {
      if (unavailable instanceof HTMLElement) {
        unavailable.hidden = false;
        unavailable.textContent = "Serviciul de lubrifiere nu este disponibil momentan.";
      }
      return;
    }

    fetchFirstSellableVariant(flow.rule.productLegacyId)
      .then((variantMeta) => {
        if (!variantMeta || !variantMeta.variantLegacyId || variantMeta.available === false) {
          setFormLubricationMetadata(form, {
            scope: flow.scope,
            ruleKey: flow.rule ? flow.rule.key : "",
            variantLegacyId: "",
          });
          checkbox.checked = false;
          checkbox.disabled = true;
          if (priceLabel) {
            priceLabel.textContent = "(indisponibil)";
          }
          if (unavailable instanceof HTMLElement) {
            unavailable.hidden = false;
            unavailable.textContent = flow.scope === LUBE_SCOPE_KEYBOARD
              ? "Serviciul de lubrifiere nu este disponibil momentan pentru acest layout."
              : "Serviciul de lubrifiere nu este disponibil momentan pentru acest produs.";
          }
          syncAndPersistState();
          return;
        }

        setFormLubricationMetadata(form, {
          scope: flow.scope,
          ruleKey: flow.rule ? flow.rule.key : "",
          variantLegacyId: variantMeta.variantLegacyId,
        });
        checkbox.disabled = false;
        if (priceLabel instanceof HTMLElement) {
          if (flow.scope === LUBE_SCOPE_SWITCH) {
            bindSwitchCheckboxPriceLabelPreview({
              form,
              priceLabel,
              lubeUnitPriceCents: variantMeta.priceCents,
            });
          } else {
            priceLabel.textContent = `(+${formatLeiShort(variantMeta.priceCents)})`;
          }
        }
        syncAndPersistState();
        bindLivePricePreview({
          node,
          form,
          checkbox,
          lubeUnitPriceCents: variantMeta.priceCents,
        });
      })
      .catch((error) => {
        console.error("[qk-lubrication-pdp] Product fetch failed", error);
        setFormLubricationMetadata(form, {
          scope: flow.scope,
          ruleKey: flow.rule ? flow.rule.key : "",
          variantLegacyId: "",
        });
        checkbox.checked = false;
        checkbox.disabled = true;
        if (priceLabel) {
          priceLabel.textContent = "(indisponibil)";
        }
        if (unavailable instanceof HTMLElement) {
          unavailable.hidden = false;
          unavailable.textContent = "Serviciul de lubrifiere nu este disponibil momentan.";
        }
        syncAndPersistState();
      });

    checkbox.addEventListener("change", () => {
      syncAndPersistState();
    });

    form.addEventListener(
      "submit",
      (event) => {
        const submitter = event && event.submitter instanceof HTMLElement ? event.submitter : null;
        syncAndPersistState();
        setAtcLoadingState(form, true, submitter);
      },
      true,
    );
  }

  function getNodeContext(node) {
    const formId = String(node.getAttribute("data-form-id") || "").trim();
    const rawNoLub = String(node.getAttribute("data-no-lubrication") || "").trim().toLowerCase();
    const rawSwitchTagEligible = String(node.getAttribute("data-switch-tag-eligible") || "").trim().toLowerCase();
    return {
      formId,
      productLegacyId: normalizeLegacyId(String(node.getAttribute("data-product-legacy-id") || "").trim()),
      productType: String(node.getAttribute("data-product-type") || "").trim(),
      layoutRaw: String(node.getAttribute("data-layout") || "").trim(),
      hotswapRaw: String(node.getAttribute("data-hotswap") || "").trim(),
      noLubrication: ["1", "true", "yes", "da"].includes(rawNoLub),
      switchTagEligible: ["1", "true", "yes", "da", "on"].includes(rawSwitchTagEligible),
    };
  }

  function bindSubmissionGuards(input) {
    const form = input && input.form instanceof HTMLFormElement ? input.form : null;
    const checkbox = input && input.checkbox instanceof HTMLInputElement ? input.checkbox : null;
    const flow = input && typeof input.flow === "object" ? input.flow : null;
    const context = input && typeof input.context === "object" ? input.context : null;
    const getRuleKey = typeof (input && input.getRuleKey) === "function" ? input.getRuleKey : () => "";
    const getLayoutKey = typeof (input && input.getLayoutKey) === "function" ? input.getLayoutKey : () => "";
    const primeState = typeof (input && input.primeState) === "function" ? input.primeState : () => {};

    if (!form || !checkbox || !flow || !context || form.dataset.qkLubeSubmitGuardBound === "1") {
      return;
    }

    form.dataset.qkLubeSubmitGuardBound = "1";

    const primeIntent = () => {
      primeState();

      const resolvedFlow = {
        scope: flow.scope || "",
        rule: flow.rule || null,
        layoutKey: String(getLayoutKey() || flow.layoutKey || "").trim(),
      };

      if (resolvedFlow.rule && !resolvedFlow.rule.key) {
        resolvedFlow.rule = {
          ...resolvedFlow.rule,
          key: String(getRuleKey() || "").trim(),
        };
      }

      persistPendingLubricationIntent({
        selected: checkbox.checked && !checkbox.disabled,
        form,
        context,
        flow: resolvedFlow,
      });
    };

    const submitButtons = resolveAssociatedSubmitButtons(form);
    submitButtons.forEach((button) => {
      button.addEventListener(
        "pointerdown",
        () => {
          primeIntent();
        },
        true,
      );

      button.addEventListener(
        "click",
        () => {
          primeIntent();
        },
        true,
      );
    });

    form.addEventListener(
      "keydown",
      (event) => {
        if (!event || event.key !== "Enter") {
          return;
        }
        primeIntent();
      },
      true,
    );
  }

  function resolveAssociatedSubmitButtons(form) {
    if (!(form instanceof HTMLFormElement)) {
      return [];
    }

    const buttons = [];
    const seen = new Set();
    const formId = String(form.id || "").trim();

    const pushUnique = (element) => {
      if (!(element instanceof HTMLButtonElement)) {
        return;
      }

      if (seen.has(element)) {
        return;
      }

      seen.add(element);
      buttons.push(element);
    };

    form.querySelectorAll('button[type="submit"]').forEach((button) => {
      pushUnique(button);
    });

    if (formId) {
      document.querySelectorAll(`button[type="submit"][form="${cssEscape(formId)}"]`).forEach((button) => {
        pushUnique(button);
      });
    }

    return buttons;
  }

  function setAtcLoadingState(form, isLoading, submitter) {
    const buttons = resolveAssociatedSubmitButtons(form);
    if (!buttons.length) {
      return;
    }

    buttons.forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) {
        return;
      }

      if (isLoading) {
        button.setAttribute("data-qk-atc-loading", "true");
      } else {
        button.removeAttribute("data-qk-atc-loading");
      }
    });

    if (isLoading && submitter instanceof HTMLButtonElement) {
      submitter.setAttribute("data-qk-atc-loading", "true");
    }

    if (isLoading) {
      window.setTimeout(() => {
        buttons.forEach((button) => {
          if (button instanceof HTMLButtonElement) {
            button.removeAttribute("data-qk-atc-loading");
          }
        });
      }, 8000);
    }
  }

  function persistPendingLubricationIntent(input) {
    const payload = input && typeof input === "object" ? input : {};
    const form = payload.form instanceof HTMLFormElement ? payload.form : null;
    const context = payload.context && typeof payload.context === "object" ? payload.context : {};
    const flow = payload.flow && typeof payload.flow === "object" ? payload.flow : {};
    const selected = Boolean(payload.selected);

    if (!selected || !form) {
      clearPendingLubricationIntent();
      return;
    }

    const scope = String(flow.scope || "").trim().toLowerCase();
    if (![LUBE_SCOPE_KEYBOARD, LUBE_SCOPE_SWITCH].includes(scope)) {
      clearPendingLubricationIntent();
      return;
    }

    const productLegacyId = normalizeLegacyId(context.productLegacyId);
    const ruleKey = String((flow.rule && flow.rule.key) || "").trim();
    const layoutKey = scope === LUBE_SCOPE_KEYBOARD ? String(flow.layoutKey || "").trim() : "";
    const variantLegacyId = normalizeLegacyId(getCurrentVariantIdFromForm(form));
    const quantity = Math.max(1, parseInteger(getQuantityFromForm(form), 1));

    if (!productLegacyId || !ruleKey) {
      clearPendingLubricationIntent();
      return;
    }

    const snapshot = {
      version: 1,
      selected: true,
      createdAt: Date.now(),
      productLegacyId,
      variantLegacyId,
      quantity,
      scope,
      ruleKey,
      layoutKey,
    };

    try {
      window.sessionStorage.setItem(LUBE_INTENT_STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
      // ignore storage failures
    }
  }

  function clearPendingLubricationIntent() {
    try {
      window.sessionStorage.removeItem(LUBE_INTENT_STORAGE_KEY);
    } catch {
      // ignore storage failures
    }
  }

  function getCurrentVariantIdFromForm(form) {
    if (!(form instanceof HTMLFormElement)) {
      return "";
    }

    const enabledInput = resolveAssociatedFormControl(
      form,
      (control) => (control instanceof HTMLInputElement || control instanceof HTMLSelectElement) &&
        control.name === "id" &&
        !control.disabled,
    );
    if (enabledInput instanceof HTMLInputElement || enabledInput instanceof HTMLSelectElement) {
      return String(enabledInput.value || "").trim();
    }

    const fallbackInput = resolveAssociatedFormControl(
      form,
      (control) => (control instanceof HTMLInputElement || control instanceof HTMLSelectElement) && control.name === "id",
    );
    if (fallbackInput instanceof HTMLInputElement || fallbackInput instanceof HTMLSelectElement) {
      return String(fallbackInput.value || "").trim();
    }

    return "";
  }

  function getQuantityFromForm(form) {
    if (!(form instanceof HTMLFormElement)) {
      return "1";
    }

    return String(resolveCurrentQuantityFromForm(form));
  }

  function ensureFormPropertyInputs(form) {
    return ensureFormPropertyInputMap(form, "_qk_lube_", "data-qk-lube-form-prop");
  }

  function clearLegacyPublicFormPropertyInputs(form) {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const legacyInputs = Array.from(
      form.querySelectorAll('[data-qk-lube-form-prop-public], input[name^="properties[qk_lube_"]'),
    );

    legacyInputs.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) {
        return;
      }
      input.value = "";
      input.disabled = true;
      input.remove();
    });
  }

  function ensureFormPropertyInputMap(form, propertyPrefix, dataAttrName) {
    const map = {};
    PROP_KEYS.forEach((propertyName) => {
      const selector = `[${dataAttrName}="${propertyName}"]`;
      let input = form.querySelector(selector);
      if (!(input instanceof HTMLInputElement)) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = `properties[${propertyPrefix}${propertyName}]`;
        input.setAttribute(dataAttrName, propertyName);
        form.appendChild(input);
      }
      input.disabled = false;
      map[propertyName] = input;
    });
    return map;
  }

  function clearFormPropertyInputs() {
    Array.from(arguments).forEach((inputsByKey) => {
      const inputs = inputsByKey && typeof inputsByKey === "object" ? inputsByKey : {};
      PROP_KEYS.forEach((propertyName) => {
        const input = inputs[propertyName];
        if (input instanceof HTMLInputElement) {
          input.value = "";
        }
      });
    });
  }

  function syncPropertyState(inputsByKey, payload) {
    const selectedValue = payload.selected ? "1" : "0";
    const scopeValue = payload.scope || "";
    const ruleValue = payload.ruleKey || "";
    const layoutValue = payload.layoutKey || "";

    setPropertyValue(inputsByKey, PROP_SELECTED, selectedValue);
    setPropertyValue(inputsByKey, PROP_SCOPE, scopeValue);
    setPropertyValue(inputsByKey, PROP_RULE, ruleValue);
    setPropertyValue(inputsByKey, PROP_LAYOUT, layoutValue);
  }

  function setPropertyValue(inputsByKey, propertyName, value) {
    const input = inputsByKey && typeof inputsByKey === "object" ? inputsByKey[propertyName] : null;
    if (input instanceof HTMLInputElement) {
      input.value = String(value == null ? "" : value);
    }
  }

  function setFormLubricationMetadata(form, input) {
    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const payload = input && typeof input === "object" ? input : {};
    const scope = String(payload.scope || "").trim().toLowerCase();
    const ruleKey = String(payload.ruleKey || "").trim();
    const variantLegacyId = normalizeLegacyId(payload.variantLegacyId);

    if (scope) {
      form.setAttribute("data-qk-lube-scope", scope);
    } else {
      form.removeAttribute("data-qk-lube-scope");
    }

    if (ruleKey) {
      form.setAttribute("data-qk-lube-rule", ruleKey);
    } else {
      form.removeAttribute("data-qk-lube-rule");
    }

    if (variantLegacyId) {
      form.setAttribute("data-qk-lube-variant-id", variantLegacyId);
    } else {
      form.removeAttribute("data-qk-lube-variant-id");
    }
  }

  function setDynamicCheckoutDisabled(formId, shouldDisable) {
    if (!formId) {
      return;
    }

    const buyButtonsNodes = Array.from(document.querySelectorAll(`buy-buttons[form="${cssEscape(formId)}"]`));
    if (!buyButtonsNodes.length) {
      return;
    }

    buyButtonsNodes.forEach((buyButtons) => {
      if (!(buyButtons instanceof HTMLElement)) {
        return;
      }

      if (shouldDisable) {
        buyButtons.setAttribute("data-qk-dynamic-checkout-disabled", "1");
      } else {
        buyButtons.removeAttribute("data-qk-dynamic-checkout-disabled");
      }
    });
  }

  function fetchFirstSellableVariant(productLegacyId) {
    const productId = normalizeLegacyId(productLegacyId);
    if (!productId) {
      return Promise.resolve(null);
    }

    if (Object.prototype.hasOwnProperty.call(variantCacheByProductId, productId)) {
      return Promise.resolve(variantCacheByProductId[productId]);
    }

    return fetchProductByLegacyId(productId).then((product) => {
      const variants = Array.isArray(product && product.variants) ? product.variants : [];
      const preferred = variants.find((variant) => variant && variant.available !== false) || variants[0] || null;

      const resolved = preferred
        ? {
            variantLegacyId: normalizeLegacyId(preferred.id),
            available: preferred.available !== false,
            priceCents: parsePriceToCents(preferred.price),
          }
        : null;

      variantCacheByProductId[productId] = resolved;
      return resolved;
    });
  }

  async function fetchProductByLegacyId(productLegacyId) {
    const productId = normalizeLegacyId(productLegacyId);
    if (!productId) {
      return null;
    }

    if (Object.prototype.hasOwnProperty.call(productLookupCacheById, productId)) {
      return productLookupCacheById[productId];
    }

    const requestedByIdsEndpoint = await fetchProductByLegacyIdFromIdsEndpoint(productId);
    if (requestedByIdsEndpoint) {
      productLookupCacheById[productId] = requestedByIdsEndpoint;
      return requestedByIdsEndpoint;
    }

    const fallbackFromCatalogScan = await fetchProductByLegacyIdFromCatalogScan(productId);
    productLookupCacheById[productId] = fallbackFromCatalogScan || null;
    return fallbackFromCatalogScan || null;
  }

  async function fetchProductByLegacyIdFromIdsEndpoint(productLegacyId) {
    const endpoint = `${getRootPath()}products.json?ids=${encodeURIComponent(productLegacyId)}&limit=1`;

    try {
      const response = await fetch(endpoint, {
        credentials: "same-origin",
      });

      if (!response.ok) {
        return null;
      }

      const payload = await response.json();
      const products = Array.isArray(payload && payload.products) ? payload.products : [];
      return (
        products.find((product) => normalizeLegacyId(product && product.id) === productLegacyId)
        || null
      );
    } catch {
      return null;
    }
  }

  async function fetchProductByLegacyIdFromCatalogScan(productLegacyId) {
    for (let page = 1; page <= PRODUCT_LOOKUP_MAX_PAGES; page += 1) {
      const endpoint = `${getRootPath()}products.json?limit=${PRODUCT_LOOKUP_PAGE_LIMIT}&page=${page}`;
      let payload = null;

      try {
        const response = await fetch(endpoint, {
          credentials: "same-origin",
        });
        if (!response.ok) {
          break;
        }
        payload = await response.json();
      } catch {
        break;
      }

      const products = Array.isArray(payload && payload.products) ? payload.products : [];
      if (!products.length) {
        break;
      }

      const exactMatch = products.find((product) => normalizeLegacyId(product && product.id) === productLegacyId);
      if (exactMatch) {
        return exactMatch;
      }

      if (products.length < PRODUCT_LOOKUP_PAGE_LIMIT) {
        break;
      }
    }

    return null;
  }

  function resolveKeyboardMapping(layoutRaw, rules) {
    const normalizedRules = Array.isArray(rules) ? rules : [];
    if (!normalizedRules.length) {
      return null;
    }

    const candidates = buildLayoutCandidates(layoutRaw);
    if (!candidates.length) {
      return null;
    }

    for (const candidate of candidates) {
      for (const rule of normalizedRules) {
        if ((rule.aliases || []).includes(candidate)) {
          return {
            rule,
            layoutKey: candidate,
          };
        }
      }
    }

    return null;
  }

  function resolveSwitchRule(rules) {
    const normalizedRules = Array.isArray(rules) ? rules : [];
    if (!normalizedRules.length) {
      return null;
    }

    const byAlias = normalizedRules.find((rule) => Array.isArray(rule.aliases) && rule.aliases.includes(SWITCH_TAG_ALIAS));
    if (byAlias) {
      return byAlias;
    }

    const byKey = normalizedRules.find((rule) => normalizeAlias(rule && rule.key) === SWITCH_TAG_ALIAS);
    return byKey || normalizedRules[0];
  }

  function buildLayoutCandidates(rawLayout) {
    const raw = String(rawLayout || "").trim().toLowerCase();
    if (!raw) {
      return [];
    }

    const tokens = new Set();
    const compact = raw.replace(/\s+/g, " ").trim();
    const collapsed = compact.replace(/\s+/g, "");

    tokens.add(compact);
    tokens.add(collapsed);
    tokens.add(collapsed.replace(/[()%_-]+/g, ""));
    tokens.add(collapsed.replace(/%/g, ""));
    tokens.add(compact.replace(/\(tkl\)/g, "").trim());

    const numericMatch = raw.match(/\b(60|65|75|80|100)\b/);
    if (numericMatch && numericMatch[1]) {
      tokens.add(numericMatch[1]);
      tokens.add(`${numericMatch[1]}%`);
    }

    if (raw.includes("tkl")) {
      tokens.add("tkl");
    }

    return Array.from(tokens)
      .map((token) => normalizeAlias(token))
      .filter(Boolean);
  }

  function normalizeConfig(raw) {
    const source = raw && typeof raw === "object" ? raw : {};
    const keyboard = source.keyboard && typeof source.keyboard === "object" ? source.keyboard : {};
    const switchConfig = source.switch && typeof source.switch === "object" ? source.switch : {};
    const ui = source.ui && typeof source.ui === "object" ? source.ui : {};

    const normalizedKeyboardRules = Array.isArray(keyboard.rules)
      ? keyboard.rules
          .map((rule) => {
            const value = rule && typeof rule === "object" ? rule : {};
            const key = String(value.key || "").trim();
            const productLegacyId = normalizeLegacyId(value.productLegacyId || value.productLegacyID || "");
            const aliases = Array.isArray(value.aliases)
              ? value.aliases.map((entry) => normalizeAlias(entry)).filter(Boolean)
              : [];

            if (!key || !productLegacyId || !aliases.length) {
              return null;
            }

            return {
              key,
              productLegacyId,
              aliases: Array.from(new Set(aliases)),
            };
          })
          .filter(Boolean)
      : [];

    const normalizedSwitchRules = Array.isArray(switchConfig.rules)
      ? switchConfig.rules
          .map((rule) => {
            const value = rule && typeof rule === "object" ? rule : {};
            const key = String(value.key || "").trim();
            const productLegacyId = normalizeLegacyId(value.productLegacyId || value.productLegacyID || "");
            const aliases = Array.isArray(value.aliases)
              ? value.aliases.map((entry) => normalizeAlias(entry)).filter(Boolean)
              : [];
            const normalizedAliases = Array.from(new Set((aliases.length ? aliases : [normalizeAlias(key)]).filter(Boolean)));

            if (!key || !productLegacyId || !normalizedAliases.length) {
              return null;
            }

            return {
              key,
              productLegacyId,
              aliases: normalizedAliases,
            };
          })
          .filter(Boolean)
      : [];

    return {
      enabled: toBoolean(source.enabled, true),
      keyboard: {
        enabled: toBoolean(keyboard.enabled, true),
        rules: normalizedKeyboardRules,
      },
      switch: {
        enabled: toBoolean(switchConfig.enabled, false),
        rules: normalizedSwitchRules,
      },
      ui: {
        label_ro: String(ui.label_ro || "Lubrifiere premium pentru switch-uri").trim(),
      },
    };
  }

  function bindSwitchCheckboxPriceLabelPreview(input) {
    const form = input && input.form instanceof HTMLFormElement ? input.form : null;
    const priceLabel = input && input.priceLabel instanceof HTMLElement ? input.priceLabel : null;
    const lubeUnitPriceCents = parseInteger(input && input.lubeUnitPriceCents, 0);

    if (!form || !priceLabel || lubeUnitPriceCents <= 0) {
      return;
    }

    if (priceLabel.dataset.qkLubeSwitchPriceBound === "1") {
      return;
    }
    priceLabel.dataset.qkLubeSwitchPriceBound = "1";

    const update = () => {
      const quantity = resolveCurrentQuantityFromForm(form);
      priceLabel.textContent = `(+${formatLeiShort(lubeUnitPriceCents * quantity)})`;
    };

    bindQuantityChangeListeners(form, update);

    update();
  }

  function parseJsonScript(scriptId) {
    const element = document.getElementById(scriptId);
    if (!(element instanceof HTMLScriptElement)) {
      return null;
    }

    const raw = String(element.textContent || "").trim();
    if (!raw || raw === "null") {
      return null;
    }

    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error("[qk-lubrication-pdp] Invalid JSON config", error);
      return null;
    }
  }

  function normalizeLegacyId(value) {
    const normalized = String(value == null ? "" : value).trim();
    if (/^\d+$/.test(normalized)) {
      return normalized;
    }

    const gidMatch = normalized.match(/\/(\d+)\s*$/);
    return gidMatch ? gidMatch[1] : "";
  }

  function normalizeAlias(value) {
    return String(value == null ? "" : value)
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "")
      .replace(/\(tkl\)/g, "tkl");
  }

  function isTruthyHotswap(value) {
    const raw = String(value == null ? "" : value)
      .trim()
      .toLowerCase();

    if (!raw) {
      return false;
    }

    const token = raw.split(/[\s,/()-]+/).filter(Boolean)[0] || "";
    return ["da", "yes", "ja", "si", "true", "1", "on"].includes(token);
  }

  function toBoolean(value, fallback) {
    if (typeof value === "boolean") {
      return value;
    }
    if (typeof value === "number") {
      return value !== 0;
    }
    if (typeof value === "string") {
      const normalized = value.trim().toLowerCase();
      if (["1", "true", "yes", "da", "on"].includes(normalized)) {
        return true;
      }
      if (["0", "false", "no", "nu", "off"].includes(normalized)) {
        return false;
      }
    }
    return Boolean(fallback);
  }

  function parseInteger(value, fallback) {
    const parsed = Number.parseInt(String(value == null ? "" : value), 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function parsePriceToCents(value) {
    if (typeof value === "number" && Number.isFinite(value)) {
      if (value > 9999) {
        return Math.round(value);
      }
      return Math.round(value * 100);
    }

    const raw = String(value == null ? "" : value).trim();
    if (!raw) {
      return 0;
    }

    const normalized = raw.replace(",", ".");
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) {
      return 0;
    }

    if (raw.includes(".") || raw.includes(",")) {
      return Math.round(parsed * 100);
    }

    if (parsed > 9999) {
      return Math.round(parsed);
    }

    return Math.round(parsed * 100);
  }

  function parsePriceTextToCents(value) {
    const raw = String(value == null ? "" : value).trim();
    if (!raw) {
      return 0;
    }

    const compact = raw.replace(/\s+/g, "");
    const numericToken = compact.replace(/[^0-9,.-]/g, "");
    if (!numericToken) {
      return 0;
    }

    const lastComma = numericToken.lastIndexOf(",");
    const lastDot = numericToken.lastIndexOf(".");
    let normalized = numericToken;

    if (lastComma >= 0 && lastDot >= 0) {
      if (lastComma > lastDot) {
        normalized = numericToken.replace(/\./g, "").replace(",", ".");
      } else {
        normalized = numericToken.replace(/,/g, "");
      }
    } else if (lastComma >= 0) {
      normalized = numericToken.replace(/\./g, "").replace(",", ".");
    } else {
      const dotCount = (numericToken.match(/\./g) || []).length;
      if (dotCount > 1) {
        normalized = numericToken.replace(/\./g, "");
      }
    }

    const parsed = Number(normalized);
    if (!Number.isFinite(parsed)) {
      return 0;
    }

    return Math.round(parsed * 100);
  }

  function formatLeiShort(cents) {
    const amount = Math.max(0, parseInteger(cents, 0)) / 100;
    const rounded = Math.round(amount * 100);
    const isWhole = rounded % 100 === 0;
    if (isWhole) {
      return `${Math.round(amount)} lei`;
    }
    return `${amount.toFixed(2).replace(".", ",")} lei`;
  }

  function bindLivePricePreview(input) {
    const node = input && input.node instanceof HTMLElement ? input.node : null;
    const form = input && input.form instanceof HTMLFormElement ? input.form : null;
    const checkbox = input && input.checkbox instanceof HTMLInputElement ? input.checkbox : null;
    const lubeUnitPriceCents = parseInteger(input && input.lubeUnitPriceCents, 0);

    if (!node || !form || !checkbox || lubeUnitPriceCents <= 0) {
      return;
    }

    if (node.dataset.qkLubePriceBound === "1") {
      return;
    }
    node.dataset.qkLubePriceBound = "1";

    const initialSaleElement = resolveSalePriceElement(form, node);
    const initialCompareElement = resolveComparePriceElement(form, node);
    const fallbackBasePriceCents = parsePriceTextToCents(initialSaleElement ? initialSaleElement.textContent : "");
    const fallbackCompareCents = parsePriceTextToCents(initialCompareElement ? initialCompareElement.textContent : "");

    const state = {
      basePriceCents: parseInteger(window.currentProductPrice, fallbackBasePriceCents),
      baseCompareAtCents: parseInteger(window.currentProductCompareAt, fallbackCompareCents),
    };

    const update = () => {
      const saleElement = resolveSalePriceElement(form, node);
      if (!(saleElement instanceof Element)) {
        return;
      }

      const compareElement = resolveComparePriceElement(form, node);
      const quantity = resolveCurrentQuantityFromForm(form);
      const lubeDeltaCents = checkbox.checked && !checkbox.disabled ? lubeUnitPriceCents * quantity : 0;
      const finalSaleCents = Math.max(0, state.basePriceCents * quantity + lubeDeltaCents);
      setPriceElementValue(saleElement, formatPriceCents(finalSaleCents));

      if (compareElement instanceof Element && state.baseCompareAtCents > state.basePriceCents) {
        const finalCompareCents = Math.max(0, state.baseCompareAtCents * quantity + lubeDeltaCents);
        compareElement.hidden = false;
        setPriceElementValue(compareElement, formatPriceCents(finalCompareCents));
      } else if (compareElement instanceof Element) {
        compareElement.hidden = true;
      }
    };

    checkbox.addEventListener("change", update);
    bindQuantityChangeListeners(form, update);

    form.addEventListener("variant:change", (event) => {
      const variant = event && event.detail ? event.detail.variant : null;
      if (!variant || typeof variant !== "object") {
        return;
      }

      state.basePriceCents = parsePriceToCents(variant.price);
      state.baseCompareAtCents = parsePriceToCents(variant.compare_at_price);
      window.currentProductPrice = state.basePriceCents;
      window.currentProductCompareAt = state.baseCompareAtCents;
      update();
    });

    update();
  }

  function resolveQuantityInput(form) {
    const quantityInputs = resolveQuantityInputs(form);
    if (!quantityInputs.length) {
      return null;
    }

    const visibleInput = quantityInputs.find((input) => isElementActuallyVisible(input));
    return visibleInput || quantityInputs[0];
  }

  function resolveQuantityInputs(form) {
    if (!(form instanceof HTMLFormElement)) {
      return [];
    }

    const inputs = [];
    const seen = new Set();

    const pushUnique = (candidate) => {
      if (!(candidate instanceof HTMLInputElement) || candidate.name !== "quantity") {
        return;
      }

      if (seen.has(candidate)) {
        return;
      }

      seen.add(candidate);
      inputs.push(candidate);
    };

    const controls = Array.isArray(form.elements) ? form.elements : Array.from(form.elements || []);
    controls.forEach((control) => {
      pushUnique(control);
    });

    form.querySelectorAll('input[name="quantity"]').forEach((input) => {
      pushUnique(input);
    });

    return inputs;
  }

  function resolveCurrentQuantityFromForm(form) {
    const quantityInput = resolveQuantityInput(form);
    if (quantityInput instanceof HTMLInputElement) {
      return Math.max(1, parseInteger(quantityInput.value, 1));
    }

    return 1;
  }

  function bindQuantityChangeListeners(form, update) {
    if (!(form instanceof HTMLFormElement) || typeof update !== "function") {
      return;
    }

    const quantityInputs = resolveQuantityInputs(form);
    const boundSelectors = new Set();
    quantityInputs.forEach((quantityInput) => {
      if (!(quantityInput instanceof HTMLInputElement)) {
        return;
      }

      quantityInput.addEventListener("input", update);
      quantityInput.addEventListener("change", update);
      quantityInput.addEventListener("blur", update);

      const selector = quantityInput.closest("quantity-selector");
      if (!(selector instanceof Element) || boundSelectors.has(selector)) {
        return;
      }

      boundSelectors.add(selector);
      selector.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
          return;
        }

        if (!target.closest(".quantity-selector__button")) {
          return;
        }

        window.requestAnimationFrame(update);
      });
    });
  }

  function isElementActuallyVisible(element) {
    if (!(element instanceof Element)) {
      return false;
    }

    if (!element.isConnected || element.getClientRects().length === 0) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === "none" || style.visibility === "hidden") {
      return false;
    }

    return true;
  }

  function resolveAssociatedFormControl(form, predicate) {
    if (!(form instanceof HTMLFormElement) || typeof predicate !== "function") {
      return null;
    }

    const controls = Array.isArray(form.elements) ? form.elements : Array.from(form.elements || []);
    for (const control of controls) {
      if (predicate(control)) {
        return control;
      }
    }

    return null;
  }

  function resolveSalePriceElement(form, node) {
    const searchRoot = resolvePriceSearchRoot(form, node);
    const formId = form && form.id ? cssEscape(form.id) : "";

    if (formId) {
      const byFormMoney = searchRoot.querySelector(`sale-price[form="${formId}"] .transcy-money`);
      if (byFormMoney instanceof Element) {
        return byFormMoney;
      }

      const byForm = searchRoot.querySelector(`sale-price[form="${formId}"]`);
      if (byForm instanceof Element) {
        return byForm;
      }
    }

    return (
      searchRoot.querySelector(".rating-with-text sale-price .transcy-money")
      || searchRoot.querySelector("sale-price .transcy-money")
      || searchRoot.querySelector(".rating-with-text .price-list sale-price")
      || searchRoot.querySelector("sale-price")
      || null
    );
  }

  function resolveComparePriceElement(form, node) {
    const searchRoot = resolvePriceSearchRoot(form, node);
    const formId = form && form.id ? cssEscape(form.id) : "";

    if (formId) {
      const byFormMoney = searchRoot.querySelector(`compare-at-price[form="${formId}"] .transcy-money`);
      if (byFormMoney instanceof Element) {
        return byFormMoney;
      }

      const byForm = searchRoot.querySelector(`compare-at-price[form="${formId}"]`);
      if (byForm instanceof Element) {
        return byForm;
      }
    }

    return (
      searchRoot.querySelector("compare-at-price .transcy-money")
      || searchRoot.querySelector(".rating-with-text compare-at-price .transcy-money")
      || searchRoot.querySelector("compare-at-price")
      || null
    );
  }

  function resolvePriceSearchRoot(form, node) {
    const scopedRoot =
      (node instanceof Element && node.closest(".product-info"))
      || (form instanceof Element && form.closest(".product-info"))
      || (node instanceof Element && node.closest("safe-sticky"))
      || (form instanceof Element && form.closest("safe-sticky"))
      || null;

    if (scopedRoot instanceof Element) {
      return scopedRoot;
    }

    return document;
  }

  function setPriceElementValue(element, text) {
    if (!(element instanceof Element)) {
      return;
    }

    const normalized = String(text == null ? "" : text);
    if (element.classList.contains("transcy-money")) {
      element.textContent = normalized;
      return;
    }

    const transcyNode = element.querySelector(".transcy-money");
    if (transcyNode instanceof Element) {
      transcyNode.textContent = normalized;
      return;
    }

    let valueNode = element.querySelector("[data-qk-live-price-value]");
    if (!(valueNode instanceof Element)) {
      valueNode = document.createElement("span");
      valueNode.setAttribute("data-qk-live-price-value", "1");
      const srOnly = element.querySelector(".sr-only");

      if (srOnly instanceof Element) {
        Array.from(element.childNodes).forEach((childNode) => {
          if (childNode !== srOnly) {
            childNode.remove();
          }
        });
        element.appendChild(document.createTextNode(" "));
        element.appendChild(valueNode);
      } else {
        element.textContent = "";
        element.appendChild(valueNode);
      }
    }

    valueNode.textContent = normalized;
  }

  function formatPriceCents(cents) {
    const amount = Math.max(0, parseInteger(cents, 0)) / 100;
    const value = amount.toFixed(2).replace(".", ",");
    const symbol = String(window.currentCurrency || "").trim();
    if (symbol) {
      return `${symbol} ${value}`;
    }

    const currencyCode = String(window.currentCurrencyName || "").trim().toUpperCase();
    if (currencyCode) {
      return `${value} ${currencyCode}`;
    }

    return `${value} lei`;
  }

  function getRootPath() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return "/";
  }

  function cssEscape(value) {
    if (window.CSS && typeof window.CSS.escape === "function") {
      return window.CSS.escape(value);
    }
    return String(value).replace(/"/g, '\\"');
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
